import type { Bounds, SceneEffect } from '@manudev/assistant';
import type * as THREE from 'three';
import { create } from 'zustand';

export type PickMode = 'none' | 'arch' | 'space' | 'all';

export interface Selection {
  layer: string;
  expressID: number;
}

interface SelectionState {
  pick: PickMode;
  selected: Selection | null;
  highlighted: number[];
  heroSize: [number, number, number];
  hoverMeshes: THREE.Object3D[];
  hidden: string[];
  effects: SceneEffect[];
  focus: { center: [number, number, number]; radius: number } | null;
  labels: { id: number; text: string }[];
  bounds: Record<number, Bounds> | null;
  setPick: (pick: PickMode) => void;
  select: (sel: Selection | null) => void;
  setHighlighted: (ids: number[]) => void;
  setHeroSize: (size: [number, number, number]) => void;
  setHoverMeshes: (meshes: THREE.Object3D[]) => void;
  toggleHidden: (discipline: string) => void;
  setEffects: (effects: SceneEffect[]) => void;
  setFocus: (focus: SelectionState['focus']) => void;
  setLabels: (labels: SelectionState['labels']) => void;
  setBounds: (bounds: Record<number, Bounds> | null) => void;
}

export const useSelectionStore = create<SelectionState>((set) => ({
  pick: 'none',
  selected: null,
  highlighted: [],
  heroSize: [26, 8, 9],
  hoverMeshes: [],
  hidden: [],
  effects: [],
  focus: null,
  labels: [],
  bounds: null,
  setPick: (pick) => set((s) => (s.pick === pick ? s : { pick })),
  select: (selected) => set({ selected }),
  setHighlighted: (highlighted) => set({ highlighted }),
  setHeroSize: (heroSize) => set({ heroSize }),
  setHoverMeshes: (hoverMeshes) =>
    set((s) =>
      s.hoverMeshes.length === hoverMeshes.length && s.hoverMeshes[0] === hoverMeshes[0]
        ? s
        : { hoverMeshes },
    ),
  toggleHidden: (d) =>
    set((s) => ({
      hidden: s.hidden.includes(d) ? s.hidden.filter((x) => x !== d) : [...s.hidden, d],
    })),
  setEffects: (effects) => set({ effects }),
  setFocus: (focus) => set({ focus }),
  setLabels: (labels) => set({ labels }),
  setBounds: (bounds) => set({ bounds }),
}));
